'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'Admin' | 'Teknisi' | 'CS';

export interface AppUser {
  id: string;
  username: string;
  nama_lengkap: string;
  role: UserRole;
  is_active: boolean;
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  hasRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => {},
  signOut: () => {},
  hasRole: () => false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const SESSION_KEY = 'eoffice_session';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore session from localStorage
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed: AppUser = JSON.parse(stored);
        setUser(parsed);
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
    setLoading(false);
  }, []);

  const signIn = async (username: string, password: string) => {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('app_users')
      .select('id, username, nama_lengkap, role, is_active')
      .eq('username', username)
      .eq('password', password)
      .eq('is_active', true)
      .single();

    if (error || !data) {
      throw new Error('Username atau password salah.');
    }

    const appUser: AppUser = {
      id: data.id,
      username: data.username,
      nama_lengkap: data.nama_lengkap,
      role: data.role as UserRole,
      is_active: data.is_active,
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(appUser));
    setUser(appUser);
  };

  const signOut = () => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  };

  const hasRole = (roles: UserRole[]) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};
